import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { ChecklistItem } from '@shared/checklist/checklist.component';
import { TranslateService } from '@ngx-translate/core';
import { ClientNamespacedResourceBaseUrlBuilder } from '@core/client-namespaced-resource-base-url-builder.service';
import { ApiService } from '@core/api/api.service';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ClientVerificationResponse } from '@core/models/client-verification.model';
import { ClientDetailsService } from '@core/client-details/client-details.service';
import { AppStoreQuery } from '@core/state';
import { ClientDetails } from '@core/models/client-details.model';
import { SnackBarService } from '@feature/snack-bar/snack-bar.service';

@Injectable({
  providedIn: 'root',
})
export class ClientQuestionnaireCheckService {
  constructor(
    private translate: TranslateService,
    private urlBuilder: ClientNamespacedResourceBaseUrlBuilder,
    private apiService: ApiService,
    private detailsService: ClientDetailsService,
    private appStoreQuery: AppStoreQuery,
    private snackBarService: SnackBarService,
  ) {}

  private fetchChecklistData(): Observable<ChecklistItem[]> {
    return this.appStoreQuery.selectUser().pipe(
      switchMap((user) => {
        if (user.id) {
          return this.detailsService.get(user?.client?.uid).pipe(
            map((clientDetail: ClientDetails) => {
              const rawChecklistData: ChecklistItem[] = [
                {
                  label: this.translate.instant('INTL_FIRST_NAME'),
                  passes: true,
                  tags: ['firstname'],
                  show: true,
                },
                {
                  label: this.translate.instant('INTL_LAST_NAME'),
                  passes: true,
                  tags: ['surname'],
                  show: true,
                },
                {
                  label: this.translate.instant('INTL_BIRTHPLACE'),
                  passes: true,
                  tags: ['place_of_birth'],
                  show: true,
                },
                {
                  label: this.translate.instant('INTL_BIRTHDAY'),
                  passes: true,
                  tags: ['date_of_birth'],
                  show: true,
                },
                {
                  label: this.translate.instant('INTL_EMAIL'),
                  passes: true,
                  tags: ['email'],
                  show: true,
                },
                {
                  label: this.translate.instant('INTL_BANK_NAME'),
                  passes: true,
                  tags: ['bank_name'],
                  show: true,
                },
                {
                  label: this.translate.instant('INTL_IBAN'),
                  passes: true,
                  tags: ['bank_iban'],
                  show: true,
                },
                {
                  label: this.translate.instant('INTL_BANK_BIC'),
                  passes: true,
                  tags: ['bic'],
                  show: true,
                },
                {
                  label: this.translate.instant('INTL_TAX_RESIDENCY'),
                  passes: true,
                  tags: ['tax_resident'],
                  show: true,
                },
                {
                  label: this.translate.instant('INTL_TAX_ID'),
                  passes: true,
                  tags: ['tax_identification_number'],
                  show: true,
                },
                {
                  label: this.translate.instant('INTL_IS_ACQUIRING_INVESTMENT'),
                  passes: true,
                  tags: ['own_behalf'],
                  show: true,
                },
                {
                  label: this.translate.instant('INTL_COUNTRY'),
                  passes: true,
                  tags: ['address_country'],
                  show: true,
                },
                {
                  label: this.translate.instant('INTL_ADDRESS_POSTAL_CODE'),
                  passes: true,
                  tags: ['address_postal_code'],
                  show: true,
                },
                {
                  label: this.translate.instant('INTL_ADDRESS_CITY'),
                  passes: true,
                  tags: ['address_city'],
                  show: true,
                },
                {
                  label: this.translate.instant('INTL_ADDRESS_STREET'),
                  passes: true,
                  tags: ['address_street'],
                  show: true,
                },
                {
                  label: this.translate.instant('INTL_POLITICALLY_EXPOSED'),
                  passes: true,
                  tags: ['politically_exposed'],
                  expectedValue: true,
                  show: true,
                },
                {
                  label: this.translate.instant('INTL_MAIN_SOURCE_OF_FUNDS'),
                  passes: true,
                  tags: ['main_source_of_funds'],
                  show: true,
                },
                {
                  label: this.translate.instant('INTL_INVESTMENT_OBJECTIVES'),
                  passes: true,
                  tags: ['investment_objective_general'],
                  show: true,
                },
                {
                  label: this.translate.instant('INTL_OIL_ARM_TRANSACTION'),
                  passes: true,
                  tags: ['oil_arm_transaction'],
                  expectedValue: false,
                  show: true,
                },
                {
                  label: this.translate.instant('INTL_BORN_USA_A'),
                  passes: true,
                  tags: ['not_born_usa'],
                  show: true,
                },
                {
                  label: this.translate.instant('INTL_BORN_USA_B'),
                  passes: true,
                  tags: ['born_usa'],
                  show: true,
                },
                {
                  label: this.translate.instant('INTL_COMPANY_NAME'),
                  passes: true,
                  tags: ['company_name'],
                  show: user?.client?.type === 'company',
                },
                {
                  label: this.translate.instant('INTL_LEI'),
                  passes: true,
                  tags: ['company_number'],
                  show: user?.client?.type === 'company',
                },
                {
                  label: this.translate.instant('INTL_IS_LISTED_ON_STOCK'),
                  passes: true,
                  tags: ['listed_stock_exchange'],
                  show: user?.client?.type === 'company',
                },
                {
                  label: this.translate.instant('INT_HAS_SOLE_REPRESENTATION'),
                  passes: true,
                  tags: ['sole_representation'],
                  show: user?.client?.type === 'company',
                },
                {
                  label: this.translate.instant('INTL_COMMERCIAL_REGISTER_EXTRACT'),
                  passes: true,
                  tags: ['commercial_register_extract'],
                  show: user?.client?.type === 'company',
                },
                {
                  label: this.translate.instant('INTL_ARTICLES_OF_ASSOCIATION_OR_SOCIAL_CONTRACT'),
                  passes: true,
                  tags: ['articles_of_assoc_or_contract'],
                  show: user?.client?.type === 'company',
                },
                {
                  label: this.translate.instant('INTL_LAST_ANNUAL_FINANCIAL_STATEMENT'),
                  passes: true,
                  tags: ['last_annual_fin_statement'],
                  show: user?.client?.type === 'company',
                },
                {
                  label: this.translate.instant('INTL_TRANSPARENCY_REGISTER'),
                  passes: true,
                  tags: ['transparency_registry'],
                  show: user?.client?.type === 'company',
                },
                {
                  label: this.translate.instant('INTL_BUSINESS_TYPE'),
                  passes: true,
                  tags: ['business_type'],
                  show: user?.client?.type === 'company',
                },
                {
                  label: this.translate.instant('INTL_TOTAL_BALANCE_SHEET'),
                  passes: true,
                  tags: ['total_balance_sheet'],
                  show:
                    user?.client?.type === 'company' && user?.client?.clientClass?.codename !== 'eligible-counterparty',
                },
                {
                  label: this.translate.instant('INTL_NET_TURNOVER'),
                  passes: true,
                  tags: ['net_turnover'],
                  show:
                    user?.client?.type === 'company' && user?.client?.clientClass?.codename !== 'eligible-counterparty',
                },
                {
                  label: this.translate.instant('INTL_OWN_FUNDS'),
                  passes: true,
                  tags: ['own_funds'],
                  show:
                    user?.client?.type === 'company' && user?.client?.clientClass?.codename !== 'eligible-counterparty',
                },
                {
                  label: this.translate.instant('INTL_UNDERSTAND_RISK'),
                  passes: true,
                  tags: ['understand_risk'],
                  expectedValue: true,
                  show: user?.client?.clientClass?.codename === 'semi-professionals',
                },
                {
                  label: this.translate.instant('INTL_ILLIQUID_WARNING'),
                  passes: true,
                  tags: ['understand_risk_closed_funds'],
                  expectedValue: true,
                  show: user?.client?.clientClass?.codename === 'semi-professionals',
                },
                {
                  label: this.translate.instant('INTL_CONFIRM_ASSIGN_FOR_SEMI_PROFESSIONAL'),
                  passes: true,
                  tags: ['waiver_agree_semi'],
                  expectedValue: true,
                  show: user?.client?.clientClass?.codename === 'semi-professionals',
                },
                {
                  label: this.translate.instant('INTL_CONFIRM_ASSIGN_FOR_PROFESSIONAL'),
                  passes: true,
                  tags: ['waiver_agree'],
                  expectedValue: true,
                  show: user?.client?.clientClass?.codename === 'elective-professionals',
                },
              ];

              return this.checkUsaCitizenConflict(clientDetail, rawChecklistData);
            }),
            catchError((error) => {
              this.snackBarService.error('Error fetching client details:', error);

              return of([]);
            }),
          );
        } else {
          return of([]);
        }
      }),
    );
  }

  private checkUsaCitizenConflict(clientDetail: ClientDetails, checklist: ChecklistItem[]): ChecklistItem[] {
    const bornUsa = clientDetail.bornUsa;
    const notBornUsa = clientDetail.notBornUsa;

    const conflictMessage =
      bornUsa && notBornUsa
        ? this.translate.instant('INTL_TAX_RESIDENT_MUTUAL_ERROR')
        : !bornUsa && !notBornUsa
        ? this.translate.instant('INTL_NO_USA_CITIZENS')
        : '';

    return checklist.map((item) => {
      if (item.tags.includes('born_usa') || item.tags.includes('not_born_usa')) {
        item.expectedValueText = conflictMessage ? conflictMessage : '';
      }
      return this.addExpectedValueToLabel(item);
    });
  }

  private addExpectedValueToLabel(item: ChecklistItem): ChecklistItem {
    if (item.expectedValue !== undefined) {
      item.label = this.translate.instant(item.label);
      item.expectedValueText = `${this.translate.instant('INTL_EXPECTED_VALUE')}: `;
      item.expectedValueText += item.expectedValue
        ? this.translate.instant('INTL_YES')
        : this.translate.instant('INTL_NO');
    } else {
      item.label = this.translate.instant(item.label);
    }
    return item;
  }

  getChecklistData(): Observable<ChecklistItem[]> {
    return this.fetchChecklistData();
  }

  getIncompleteFields(): Observable<string[]> {
    const url = this.urlBuilder.buildBaseUrl('verify-questionnaire');

    // Guard: Don't make API call if no valid URL (e.g., during logout)
    if (!url) {
      return of([]);
    }

    return this.apiService.get<ClientVerificationResponse>(url).pipe(
      map((response) => response.incompleteFields || []),
      catchError((error) => {
        this.snackBarService.error('Error fetching incomplete fields:', error);
        return of([]);
      }),
    );
  }
}
